import React, { CSSProperties, HTMLAttributes, ReactNode, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import { MGText } from "../MGText";
import { MGTitle } from "../MGTitle";
import styles from "./MGAccordion.module.css";

export const MGAccordionAppearance = {
  white: "white",
  "light-grey": "light-grey",
} as const;

export const MGAccordionSpacing = {
  none: "0px",
  pico: "2px",
  nano: "4px",
  micro: "8px",
  mini: "12px",
  xxxs: "16px",
  xxs: "20px",
  xs: "24px",
  sm: "32px",
  md: "40px",
  lg: "48px",
  xl: "56px",
  xxl: "64px",
  xxxl: "72px",
  big: "80px",
  huge: "88px",
  giant: "96px",
} as const;

export type TMGAccordionAppearance = keyof typeof MGAccordionAppearance;
export type TMGAccordionSpacing = keyof typeof MGAccordionSpacing;
export type TMGAccordionBadgeBackground = "light" | "dark" | "brand";

export type TMGAccordionBadgeProps = {
  amount?: number;
  background?: TMGAccordionBadgeBackground;
  className?: string;
  dataTestId?: string;
};

export type TMGAccordionItem = {
  badge?: TMGAccordionBadgeProps;
  children?: ReactNode;
  className?: string;
  dataTestId?: string;
  id?: string;
  onToggle?: (expanded: boolean) => void;
  scrollIntoView?: boolean;
  text?: string;
  title: ReactNode;
};

export type TMGAccordionProps = Omit<HTMLAttributes<HTMLDivElement>, "children" | "title"> & {
  appearance?: TMGAccordionAppearance;
  dataTestId?: string;
  defaultExpandedIndex?: number | null;
  expandedIndex?: number | null;
  forceSmallSizeComponent?: boolean;
  gap?: TMGAccordionSpacing;
  items?: TMGAccordionItem[];
  onExpandedChange?: (index: number | null) => void;
  title?: string;
};

type TAccordionStyle = CSSProperties & { "--mg-accordion-gap": string };

let accordionId = 0;

export const MGAccordion = ({
  appearance = "white",
  className,
  dataTestId,
  defaultExpandedIndex = null,
  expandedIndex,
  forceSmallSizeComponent = false,
  gap = "micro",
  items = [],
  onExpandedChange,
  style,
  title,
  ...htmlProps
}: TMGAccordionProps) => {
  const [internalExpandedIndex, setInternalExpandedIndex] = useState<number | null>(
    defaultExpandedIndex,
  );
  const [instanceId] = useState(() => `mg-accordion-${++accordionId}`);
  const controlled = expandedIndex !== undefined;
  const currentExpandedIndex = controlled ? expandedIndex : internalExpandedIndex;

  const toggleItem = (index: number, item: TMGAccordionItem) => {
    const nextIndex = currentExpandedIndex === index ? null : index;
    if (!controlled) setInternalExpandedIndex(nextIndex);
    onExpandedChange?.(nextIndex);
    item.onToggle?.(nextIndex === index);
  };

  const accordionStyle: TAccordionStyle = {
    "--mg-accordion-gap": MGAccordionSpacing[gap],
    ...style,
  };

  return (
    <div
      {...htmlProps}
      className={`${styles.accordion}${className ? ` ${className}` : ""}`}
      data-appearance={appearance}
      data-force-small={forceSmallSizeComponent || undefined}
      data-testid={dataTestId}
      style={accordionStyle}>
      {title && (
        <MGTitle tagName="h2" titleStyleDefinition="80-08" className={styles.listTitle}>
          {title}
        </MGTitle>
      )}
      <div className={styles.content}>
        {items.map((item, index) => {
          const itemId = item.id || `${instanceId}-${index}`;
          return (
            <MGAccordionItem
              {...item}
              key={item.id || item.dataTestId || index}
              itemId={itemId}
              expanded={currentExpandedIndex === index}
              forceSmallSizeComponent={forceSmallSizeComponent}
              appearance={appearance}
              onToggle={() => toggleItem(index, item)}
            />
          );
        })}
      </div>
    </div>
  );
};

type TMGAccordionItemProps = TMGAccordionItem & {
  appearance: TMGAccordionAppearance;
  expanded: boolean;
  forceSmallSizeComponent: boolean;
  itemId: string;
  onToggle: () => void;
};

const MGAccordionItem = ({
  appearance,
  badge,
  children,
  className,
  dataTestId,
  expanded,
  forceSmallSizeComponent,
  itemId,
  onToggle,
  scrollIntoView = false,
  text,
  title,
}: TMGAccordionItemProps) => {
  const bodyRef = useRef<HTMLDivElement>(null);
  const buttonId = `${itemId}-button`;
  const panelId = `${itemId}-panel`;

  const handleToggle = () => {
    onToggle();
    if (!expanded && scrollIntoView) {
      window.setTimeout(
        () => bodyRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }),
        200,
      );
    }
  };

  return (
    <section
      className={`${styles.item}${className ? ` ${className}` : ""}`}
      data-appearance={appearance}
      data-expanded={expanded || undefined}
      data-force-small={forceSmallSizeComponent || undefined}
      data-testid={dataTestId}>
      <button
        id={buttonId}
        type="button"
        className={styles.header}
        aria-expanded={expanded}
        aria-controls={panelId}
        onClick={handleToggle}>
        <span className={styles.itemTitle}>{title}</span>
        <span className={styles.actions}>
          {badge && <MGAccordionBadge {...badge} />}
          <ChevronDown className={styles.icon} size={24} aria-hidden="true" />
        </span>
      </button>
      <div
        ref={bodyRef}
        id={panelId}
        role="region"
        aria-labelledby={buttonId}
        aria-hidden={!expanded}
        className={styles.body}>
        <div className={styles.bodyOverflow}>
          {text && (
            <MGText
              tagName="p"
              textStyleDefinition="body-100-03"
              color="text-primary-03"
              className={styles.text}>
              {text}
            </MGText>
          )}
          {children}
        </div>
      </div>
    </section>
  );
};

const MGAccordionBadge = ({
  amount = 0,
  background = "light",
  className,
  dataTestId,
}: TMGAccordionBadgeProps) => {
  if (amount <= 0) return null;
  return (
    <span
      className={`${styles.badge}${className ? ` ${className}` : ""}`}
      data-background={background}
      data-testid={dataTestId}
      aria-label={`${amount} itens`}>
      {amount > 99 ? "+99" : amount}
    </span>
  );
};
